import { type FC, memo } from 'react';
import styles from './index.module.scss';
import ExchangeRate from './exchange-rate';
import { useModel } from 'foca';
import { BestSwapResult, ParrotCoinStruct } from 'src/types';
import { PositionUtil, BN, SplitPath, Decimal } from 'parrot-sui-sdk';
import { scaleDown } from '@utils/math';
import { globalSettingModel } from '@models/global-setting.model';
import { useParrotCoinsByType } from '@hooks/use-parrot-coins';
import { normalizeSuiCoinType, numericFormat, percentage } from '@utils/index';
import { useToggle } from 'react-use';
import parrotSdk from '@libs/parrot-sdk';
import downIcon from '@assets/images/header/down.svg';
import warnWhiteIcon from '@assets/images/parrot/warn-white.svg';
import Block from '@components/block';

interface OwnProps {
  bestRoute: BestSwapResult;
  coinA: ParrotCoinStruct;
  coinB: ParrotCoinStruct;
  loading: boolean;
}

const RouteInfo: FC<OwnProps> = (props) => {
  const { bestRoute, coinA, coinB, loading } = props;

  const [showInfo, toggleShowInfo] = useToggle(true);
  const { slippage } = useModel(globalSettingModel);
  const { coins } = useParrotCoinsByType();

  if (!bestRoute) return null;

  const slippageTolerance = percentage(Number(slippage));
  const minAmount = PositionUtil.adjustForSlippage(
    new BN(bestRoute.by_amount_in ? bestRoute.amount_out : bestRoute.amount_in),
    slippageTolerance,
    !bestRoute.by_amount_in,
  );

  const splitPath: SplitPath[] = bestRoute.paths.map((path, i) => {
    return {
      percent: 0,
      inputAmount: Number(path.amount_in),
      outputAmount: Number(path.amount_out),
      lastQuoteOutput: 0,
      pathIndex: i,
      basePaths: path.pool_ids.map((pool, index) => {
        const fromCoin = coins[normalizeSuiCoinType(path.coin_types[index]!)]!;
        const toCoin = coins[normalizeSuiCoinType(path.coin_types[index + 1]!)]!;
        const _pool = path.pools[index]!;
        return {
          direction: path.a2b[index]!,
          label: 'Parrot',
          poolAddress: pool,
          fromCoin: fromCoin.type,
          toCoin: toCoin.type,
          feeRate: Number(_pool.fee_rate),
          outputAmount: Number(path.raw_amount_limit[index + 1]),
          inputAmount: Number(path.amount_in),
          currentSqrtPrice: new BN(bestRoute.current_sqrt_price[index]!),
          fromDecimal: fromCoin.decimals,
          toDecimal: toCoin.decimals,
          currentPrice: new Decimal(fromCoin.price_usd || 0),
        };
      }),
    };
  });

  const fee = parrotSdk.Swap.calculateSwapFee(splitPath);
  const priceImpact = parrotSdk.Swap.calculateSwapPriceImpact(splitPath);
  const coin = coins[normalizeSuiCoinType(bestRoute?.coin_type_b)];

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapper_con}>
        <div className={styles.top}>
          <ExchangeRate bestRoute={bestRoute} coinA={coinA} coinB={coinB} loading={loading} />
          <img
            src={downIcon}
            data-icon
            onClick={toggleShowInfo}
            className={showInfo ? styles.icon : ''}
          />
        </div>

        {showInfo && (
          <div className={styles.route}>
            <div className={styles.swap_info}>
              <div>Swap Fee</div>
              {loading ? (
                <Block className={styles.block} />
              ) : (
                <div>
                  {fee} {coinA.symbol}
                </div>
              )}
            </div>
            <div className={styles.swap_info}>
              <div>{bestRoute.by_amount_in ? 'Minimum Received' : 'Maximum Sold'}</div>
              {loading ? (
                <Block className={styles.block} />
              ) : (
                <div>
                  {numericFormat(
                    scaleDown(
                      minAmount.toString(),
                      bestRoute.by_amount_in ? coinB.decimals : coinA.decimals,
                    ).toString(),
                    {
                      thousandSeparator: true,
                      decimalScale: 6,
                    },
                  )}{' '}
                  {coinB.symbol}
                </div>
              )}
            </div>
            <div className={styles.swap_info}>
              <div className={styles.flex}>
                Price Impact
                <img src={warnWhiteIcon} data-icon />
              </div>
              {loading ? (
                <Block className={styles.block} />
              ) : (
                <div className={Number(priceImpact) < 0 ? styles.red : ''}>
                  {Number(priceImpact).toFixed(2)}%
                </div>
              )}
            </div>
            <div className={styles.swap_info}>
              <div>Route</div>
              {loading ? (
                <Block className={styles.block} />
              ) : (
                <div>
                  {coinA.symbol}
                  {'>'}
                  {bestRoute?.coin_type_c ? `${coin?.symbol}>` : null}
                  {coinB.symbol}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(RouteInfo);
